import { generateKeyBetween } from 'fractional-indexing';
import { debounce, merge, mergeWith, set } from 'lodash';
import difference from 'lodash/difference';
import { DateTime } from 'luxon';
import { nanoid } from 'nanoid';
import { proxy } from 'valtio';
// api call imports
import {
  addComment as apiAddComment,
  archiveComment as apiArchiveComment,
  deleteMap as apiDeleteMap,
  deleteObject as apiDeleteObject,
  duplicateMap as apiDuplicateMap,
  getMap as apiGetMap,
  getMaps as apiGetMaps,
  getProject as apiGetProject,
  newMap as apiNewMap,
  newObject as apiNewObject,
  newObjects as apiNewObjects,
  undoDelete as apiUndoDelete,
  updateMap as apiUpdateMap,
  updateObject as apiUpdateObject,
} from '~/api/projects2';
import { captureTypedPosthogEvent, mapObjectTypeToCivildrawObjectType, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { compareWithArrayCopy } from '~/common/storeUtils';
// type imports
import {
  type Map as CivilDrawMap,
  EditorMode,
  LoadingState,
  type MapDetail,
  type MapObject,
  type Project,
  SortOrder,
  type TLoadedState,
  type TLoadingState,
  type UnitSystem,
} from '~/common/types';
import { type CopyMetadataOptions, MapStoreBase, type StreetViewState } from '~/components2/Map';
import rootStore from '~/store';

export const CG_CURRENT_VERSION = 'CG_CURRENT_VERSION';

export class CivilDrawStore extends MapStoreBase {
  projectId: string = '';
  project: TLoadingState | (Project & TLoadedState) = LoadingState;
  currentMap: TLoadingState | (MapDetail & TLoadedState) = LoadingState;
  currentMapId?: string;
  editorMode: EditorMode = EditorMode.None;
  hiddenObjects: string[] = [];
  sortOrder?: SortOrder = SortOrder.CreatedAt;
  undoStack: (string | undefined)[][] = [];
  redoStack: (string | undefined)[][] = [];
  // allObjectIds will track all object added to the map during a session
  allObjectIds: string[] = [];
  previousObject: string | undefined;
  shouldRedraw = false;
  printView: boolean = false;
  streetViewState: StreetViewState = { visible: false };

  get currentObject() {
    if (!this.currentMap.isLoading && this.currentObjectId) {
      return this.currentMap.objects[this.currentObjectId];
    }
    return undefined;
  }

  get objectsOnMainSurface() {
    if (!this.currentMap.isLoading) {
      if (this.currentObjectId) {
        const copy = {
          ...this.currentMap.objects,
        };
        delete copy[this.currentObjectId];
        return copy;
      } else {
        return this.currentMap.objects;
      }
    } else {
      return {};
    }
  }

  async setEditorMode(mode: EditorMode) {
    this.editorMode = mode;
    if (this.currentMap.isLoading) return;
    if (
      mode === EditorMode.EditPolygon ||
      mode === EditorMode.EditLine ||
      mode === EditorMode.EditMarker ||
      mode === EditorMode.EditText ||
      mode === EditorMode.EditTextV2
    ) {
      // Only add new objects to the undoStack.
      // First check against allObjectIds to ensure it is new.
      const allIncomingObjectIds = Object.keys(this.currentMap.objects).map((key) => key);
      // Only 1 id should arise per render.
      const newId = difference(allIncomingObjectIds, this.allObjectIds)[0];
      if (newId) {
        this.allObjectIds.push(newId);

        this.undoStack.push([undefined, JSON.stringify(this.currentObject)]);
        this.redoStack = [];
        this.redoStack = [];
      }
    }
  }

  async loadProject(projectId?: string) {
    this.projectId = projectId || this.projectId;
    this.project.isLoading = true;
    const project = (await apiGetProject(this.projectId)) as Project;
    const maps = (await apiGetMaps(this.projectId)) as Project['maps'];

    const formattedMaps: { [key: string]: CivilDrawMap } = {};

    for (const key in maps) {
      const m = maps[key];
      formattedMaps[m.id] = m;
    }

    project.maps = formattedMaps;
    this.project = { ...project, isLoading: false };
  }

  async newMap(name: string | null): Promise<string> {
    if (this.project.isLoading === false) {
      const newMap = await apiNewMap(this.projectId, name);
      this.currentMapId = newMap.id;
      this.currentMap = {
        isLoading: false,
        ...newMap,
        objects: {},
      };
      this.project.maps[this.currentMapId] = { ...this.currentMap };

      captureTypedPosthogEvent(POSTHOG_EVENTS.projectCivildrawDraftCreated, {
        project_id: this.projectId,
        organization_id: rootStore.currentOrganizationId || undefined,
        draft_id: newMap.id,
        source: 'civildraw',
      });

      return newMap.id;
    } else {
      throw 'Cannot create map when project is still loading';
    }
  }

  async deleteMap(mapId: string) {
    if (this.project.isLoading === false) {
      await apiDeleteMap(this.projectId, mapId);
      delete this.project.maps[mapId];
      this.currentMapId = undefined;

      await this.loadProject(this.projectId);
    }
  }

  async duplicateMap(mapId: string) {
    if (this.project.isLoading === false) {
      const duplicate = await apiDuplicateMap(this.projectId, mapId);
      this.project.maps[duplicate.id] = { ...duplicate };

      this.setCurrentMapId(duplicate.id);
      return duplicate.id;
    }
  }

  async newObject(o: Omit<MapObject, 'createdBy' | 'updatedBy'>, setCurrentObject = true) {
    if (!this.currentMap.isLoading) {
      const newObject = await apiNewObject(this.projectId, this.currentMap, o);

      const collapsedType = mapObjectTypeToCivildrawObjectType(o.type);
      captureTypedPosthogEvent(POSTHOG_EVENTS.projectCivildrawDrawingCreated, {
        project_id: this.projectId,
        organization_id: rootStore.currentOrganizationId || undefined,
        drawing_id: newObject.id,
        object_type: collapsedType,
        civildraw_use_new_drawing_canvas: false,
      });
      captureTypedPosthogEvent(POSTHOG_EVENTS.projectCivildrawObjectAdded, {
        project_id: this.projectId,
        organization_id: rootStore.currentOrganizationId || undefined,
        object_type: collapsedType,
        civildraw_use_new_drawing_canvas: false,
      });

      set(this.currentMap, `objects.${newObject.id}`, newObject);
      if (setCurrentObject) {
        this.setEditorMode(EditorMode.None);
        setTimeout(() => {
          this.setCurrentObjectId(newObject.id);
        }, 100);
      }
      return newObject;
    }
  }

  async addManyObjects(objs: Omit<MapObject[], 'createdBy' | 'updatedBy'>) {
    if (!this.currentMap.isLoading) {
      const newObjects = await apiNewObjects(this.projectId, this.currentMap, objs);

      newObjects.forEach((object: MapObject) => {
        set(this.currentMap, `objects.${object.id}`, object);
      });

      return newObjects;
    }
  }

  setCurrentObjectId(objectId: string) {
    if (!this.currentMap.isLoading) {
      // check if valid id
      const object = this.currentMap.objects[objectId];
      if (object) {
        this.currentObjectId = objectId;
        if (object.type === 'polygon') {
          this.setEditorMode(EditorMode.EditPolygon);
        } else if (object.type === 'line') {
          this.setEditorMode(EditorMode.EditLine);
        } else if (object.type === 'circle') {
          this.setEditorMode(EditorMode.EditCircle);
        } else if (object.type === 'marker') {
          this.setEditorMode(EditorMode.EditMarker);
        } else if (object.type === 'text') {
          this.setEditorMode(EditorMode.EditText);
        } else if (object.type === 'comment') {
          this.setEditorMode(EditorMode.EditComment);
        } else if (object.type === 'textV2') {
          this.setEditorMode(EditorMode.EditTextV2);
        } else if (object.type === 'image') {
          this.setEditorMode(EditorMode.EditImage);
        } else if (object.type === 'stamp') {
          this.setEditorMode(EditorMode.EditStamp);
        } else if (object.type === 'arrow') {
          this.setEditorMode(EditorMode.EditArrow);
        }
      }
    }
  }

  addObjectToUndoStack({ isNewUpdate }: { isNewUpdate?: boolean }) {
    this.undoStack.push([this.previousObject, JSON.stringify(this.currentObject)]);
    // get updated object data
    this.previousObject = JSON.stringify(this.currentObject);

    // if new updates are pushed to undoStack that is not part of redo() then clear out redoStack as we are on a new tangent
    if (isNewUpdate) {
      this.redoStack = [];
    }
  }

  private async remoteSyncObject(object: MapObject) {
    if (!this.currentMap.isLoading) {
      // ensure the object is still there, otherwise, it may have been deleted
      if (this.currentMap.objects[object.id]) {
        const updatedObject = await apiUpdateObject(this.projectId, this.currentMap, object);
        this.currentMap.objects[updatedObject.id] = updatedObject;

        this.addObjectToUndoStack({
          isNewUpdate: true,
        });
      }
    }
  }

  throttledRemoteSyncObject = debounce(this.remoteSyncObject, 300);

  async updateObject<T = MapObject>(o: Partial<T>) {
    if (!this.currentMap.isLoading && this.currentObjectId) {
      // update local store for immediate feedback and then sync with server

      const merged = mergeWith(this.currentObject, o, compareWithArrayCopy);
      this.currentMap.objects[this.currentObjectId!] = merged;

      this.throttledRemoteSyncObject(merged);
    }
  }

  async deleteObject<T = MapObject>(o: T, shouldPushToUndoStack: boolean = false) {
    if (!this.currentMap.isLoading) {
      delete this.currentMap.objects[(o as MapObject).id];

      // flag comes from ObjActions.tsx when the user deletes an object
      if (shouldPushToUndoStack) {
        this.undoStack.push([this.previousObject, undefined]);
        this.redoStack = [];
      }

      return apiDeleteObject(this.projectId, this.currentMap, o as MapObject);
    }
  }

  resetCurrentObjectId() {
    this.currentObjectId = undefined;
    this.setEditorMode(EditorMode.None);
  }

  resetCurrentMapState() {
    this.currentMap = LoadingState;
    this.currentMapId = '';
  }

  async updateMap(m: Partial<MapDetail>) {
    const merged = merge(this.currentMap, m) as MapDetail & TLoadedState;
    this.currentMap = merged;
    const updatedMap = await apiUpdateMap(this.projectId, merged);
    this.currentMap = merge(this.currentMap, updatedMap);
  }

  // NOTE: This helper method is for updating the name of the map on the left nav bar
  // in the left nav panel, there could be no "currentMap"/"currentMapId" set, so we
  // need to manually update the name of the desired map
  async updateMapName(mapId: string, newName: string) {
    if (this.project.isLoading === false) {
      const map = this.project.maps[mapId];

      if (map) {
        map.name = newName;
        const updatedMap = await apiUpdateMap(this.projectId, map);
        const m = merge(map, updatedMap);
        this.project.maps[mapId] = m;
      }
    }
  }

  async setCurrentMapId(mapId: string) {
    this.currentMapId = mapId;
    this.currentMap = {
      isLoading: true,
    };
    // TODO: Error handling
    const map = await apiGetMap(this.projectId, mapId);
    this.currentMap = {
      isLoading: false,
      ...map,
    };
  }

  get currentUnitSystem(): UnitSystem {
    if (this.currentMap.isLoading) {
      return 'imperial';
    } else {
      return this.currentMap.unitSystem;
    }
  }

  toggleHiddenObjects(objectId: string | string[]) {
    if (Array.isArray(objectId)) {
      this.hiddenObjects = objectId;
    } else {
      if (this.hiddenObjects.includes(objectId)) {
        this.hiddenObjects = this.hiddenObjects.filter((id) => id !== objectId);
      } else {
        this.hiddenObjects = [...this.hiddenObjects, objectId];
      }
    }
  }

  async refreshMap() {
    if (!this.currentMap.isLoading && this.currentMapId) {
      const map = await apiGetMap(this.projectId, this.currentMapId);
      this.currentMap = {
        isLoading: false,
        ...map,
      };
    }
  }

  generateSortOrderKey() {
    const objects = this.objectsOnMainSurface;
    const keys = Object.keys(objects);
    if (keys.length < 1) {
      return 'a1';
    } else {
      const lastSortOrder = objects[keys[keys.length - 1]].sortOrder;
      return generateKeyBetween(lastSortOrder, null);
    }
  }

  async restoreObject({ list, format }: { list: (string | undefined)[]; format: 'undo' | 'redo' }) {
    if (this.currentMap.isLoading) return;

    // format date values (ie. createdAt, updatedAt) from ISO to DateTime
    const previousObject: (MapObject | undefined)[] = list.map((item) => {
      if (item === undefined) return undefined;
      const previous = JSON.parse(item);
      return {
        ...previous,
        createdAt: DateTime.fromISO(previous.createdAt!, {
          outputCalendar: 'unix',
        }),
        updatedAt: DateTime.fromISO(previous.updatedAt, {
          outputCalendar: 'unix',
        }),
      };
    });

    // previously added object now needs to be deleted
    if (
      (!previousObject[0] && previousObject[1] && format === 'undo') ||
      (previousObject[0] && !previousObject[1] && format === 'redo')
    ) {
      const object = format === 'undo' ? previousObject[1]! : previousObject[0]!;
      await this.deleteObject(object);
      Reflect.deleteProperty(this.currentMap.objects, object.id);
    }

    // previously deleted object now needs to be added back in
    else if (
      (previousObject[0] && !previousObject[1] && format === 'undo') ||
      (!previousObject[0] && previousObject[1] && format === 'redo')
    ) {
      const object = format === 'undo' ? previousObject[0]! : previousObject[1]!;
      await apiUndoDelete({
        projectId: this.projectId,
        mapId: this.currentMapId!,
        objectId: object.id,
      });
      Reflect.set(this.currentMap.objects, object.id, object);
    } else if (previousObject[0] && previousObject[1]) {
      const object = format === 'undo' ? previousObject[0] : previousObject[1];
      Reflect.set(this.currentMap.objects, object.id, object);
      await apiUpdateObject(this.projectId, this.currentMap, object);
    }
    this.refreshMap();
  }

  async undo() {
    if (this.undoStack.length === 0 || this.currentMap.isLoading) return;
    this.editorMode = EditorMode.None;
    this.currentObjectId = undefined;
    const previousObjectList = this.undoStack.pop()!;
    this.redoStack.push(previousObjectList);
    await this.restoreObject({
      list: previousObjectList,
      format: 'undo',
    });
    this.shouldRedraw = true;
  }

  async redo() {
    if (this.currentMap.isLoading) return;
    const previousObjectList = this.redoStack.pop()!;
    this.undoStack.push(previousObjectList!);
    await this.restoreObject({
      list: previousObjectList,
      format: 'redo',
    });
    this.shouldRedraw = true;
  }

  async addComment(comment: string) {
    if (!this.currentMap.isLoading && this.currentMapId) {
      const updatedObject = await apiAddComment(this.projectId, this.currentMapId, this.currentObjectId!, { comment });

      this.currentMap.objects[updatedObject.id] = updatedObject;

      this.addObjectToUndoStack({
        isNewUpdate: true,
      });
    }
  }

  async archiveComment(commentId: string) {
    if (!this.currentMap.isLoading && this.currentMapId) {
      const updatedObject = await apiArchiveComment(
        this.projectId,
        this.currentMapId,
        this.currentObjectId!,
        commentId,
      );

      if (updatedObject.status === 'archived') {
        Reflect.deleteProperty(this.currentMap.objects, updatedObject.id);
      } else {
        this.currentMap.objects[updatedObject.id] = updatedObject;
      }

      this.addObjectToUndoStack({
        isNewUpdate: true,
      });
    }
  }

  async formatMaps() {}

  async copyMetadataToCanvas(title: string, options: CopyMetadataOptions) {
    const { fields, map, lngLat, color, minZoom } = options;

    const p2 = map.project([lngLat.lng, lngLat.lat]);
    const p1 = {
      x: p2.x + 100,
      y: p2.y - 100,
    };
    const c1 = map.unproject([p1.x, p1.y]);
    const coords = [c1, lngLat];

    const description = this.formatFieldsAsTable(fields);

    await this.newObject(
      {
        id: nanoid(),
        sortOrder: this.generateSortOrderKey(),
        type: 'arrow',
        title,
        description,
        geometry: {
          type: 'LineString',
          coordinates: coords.map((c) => [c.lng, c.lat]),
        },
        properties: {
          subtype: 'metadata',
          color,
          strokeOpacity: 1,
          strokeWidth: 2,
          strokeStyle: 'solid',
          font: 'Monospace',
          minZoom,
        },
      },
      false,
    );
  }

  formatFieldsAsTable(fields: { field: string; value: any }[]) {
    let text = 'Use this space\nfor detailed description';
    if (fields.length === 0) return text;

    const f1 = fields[0];
    let maxFieldLength: number = f1.field.length;
    let maxValueLength: number = f1.value.toString().length;

    for (let i = 1; i < fields.length; i++) {
      const { field, value } = fields[i];
      const fieldLength = field.length;
      const valueLength = value.toString().length;

      if (fieldLength > maxFieldLength) {
        maxFieldLength = fieldLength;
      }

      if (valueLength > maxValueLength) {
        maxValueLength = valueLength;
      }
    }

    const addSpaces = (f: string, v: string) => {
      let newField = f;
      let newValue = v;

      if (f.length < maxFieldLength) {
        const spaces = maxFieldLength - f.length;
        for (let i = 0; i < spaces; i++) {
          newField += ' ';
        }
      }

      if (v.length < maxValueLength) {
        const spaces = maxValueLength - v.length;
        for (let i = 0; i < spaces; i++) {
          newValue = ` ${newValue}`;
        }
      }

      return `${newField}\t${newValue}`;
    };

    const newF1 = addSpaces(f1.field, f1.value.toString());

    text = `${newF1}`;

    for (let i = 1; i < fields.length; i++) {
      const { field, value } = fields[i];
      const f = field;
      const v = value.toString();

      const newString = addSpaces(f, v);

      if (f === 'ID' || f === 'Parcel ID') {
        text += '\n';
      }

      text += `\n${newString}`;
    }

    const totalLength = text.split('\n')[0]?.length;
    if (totalLength) {
      const dashes = '-'.repeat(totalLength);
      text = `${dashes}\n${text}`;
    } else {
      text = `\n${text}`;
    }

    return text;
  }
}

const civildrawStore = proxy(new CivilDrawStore());
export default civildrawStore;
